import { TariffType } from '@/enums/TariffType';

const Examples = {
  floorPlans: [
    'plansData/modern/city/panoramic/topView.webp',
    'plansData/scandy/city/panoramic/topView.webp',
  ],
  interiors: [
    'plansData/modern/city/panoramic/image.webp',
    'plansData/scandy/city/panoramic/image.webp',
    'plansData/japandi/city/panoramic/image.webp',
  ],
};

/**
 * @typedef {Object} TFeature
 * @property {string} name Отображаемое название фичи.
 * @property {string} [value] Идентификатор фичи (есть только в `features`, отсутствует в `mobileFeatures`).
 * @property {string[]} [type] Модификаторы отображения, например `['special', 'double', 'color']`.
 */

/**
 * @typedef {Object} TWhatYouWillGetBlock
 * @property {string} image Путь до изображения блока.
 * @property {string[]} [images] Изображения для переключения примеров.
 * @property {string} title Заголовок блока.
 * @property {string} description Описание блока.
 * @property {string[]} featureValues Компоненты тарифа, для которых показывается блок.
 * @property {string} [linkText] Текст ссылки-примера.
 * @property {string} [linkUrl] URL ссылки-примера.
 */

/**
 * @typedef {Object} TAdditionalInfoBlock
 * @property {string} image Путь до иконки.
 * @property {string} title Подпись к иконке.
 */

/**
 * @typedef {Object} TAdditionalInfo
 * @property {string} title Заголовок блока с доп. информацией.
 * @property {TAdditionalInfoBlock[]} blocks Список пунктов доп. информации.
 */

/**
 * @typedef {Object} TFooterBlock
 * @property {string} description Текст пункта в футере.
 */

/**
 * @typedef {Object} TWhatYouWillGet
 * @property {string} titleTemplate Шаблон заголовка с плейсхолдером `{0}` для названия тарифа.
 * @property {TWhatYouWillGetBlock[]} blocks Основные блоки с описанием результата.
 * @property {TAdditionalInfo} [additionalInfo] Дополнительная информация (например, о ревизиях).
 * @property {string} footerTitle Заголовок футера.
 * @property {TFooterBlock[]} footerBlocks Блоки футера.
 */

/**
 * @typedef {Object} TTariff
 * @property {typeof TariffType[keyof typeof TariffType]} tariff Идентификатор тарифа из {@link TariffType}.
 * @property {string[]} [type] Модификаторы отображения карточки тарифа, например `['special']`.
 * @property {string} image Путь до изображения тарифа.
 * @property {'cover'|'contain'} [imageFit] Способ вписывания изображения в превью.
 * @property {string} name Название тарифа.
 * @property {string} summary Короткая формула состава тарифа.
 * @property {string} previewFeature Компонент, который открывается по клику на изображение.
 * @property {string} [bestValueText] Подпись рекомендуемого тарифа.
 * @property {'iframe'|'sphere'|'topView'|'image'} widgetType Тип виджета iframe, sphere (miniwidget), topView или image (при отсутствии)
 * @property {number} [styleCount] Количество стилей в тарифе.
 * @property {boolean} [hasEnvironmentType] Доступен ли выбор типа окружения.
 * @property {boolean} [hasBranding] Доступно ли добавление логотипа.
 * @property {TFeature[][]} features Фичи тарифа, сгруппированные по строкам (для десктопа).
 * @property {number} price Цена тарифа.
 * @property {string} placeAnOrderButtonText Текст кнопки оформления заказа.
 * @property {TWhatYouWillGet} whatYouWillGet Данные для блока «Что вы получите».
 */

/** @type {TTariff[]} */
export const en = [
  {
    tariff: TariffType.BasicSet,
    image: 'showcase/new/basic.webp',
    imageFit: 'contain',
    name: 'Basic',
    summary: 'Basic set: 2D + 3D floor plans',
    previewFeature: '2d',
    widgetType: 'topView',
    hasEnvironmentType: true,
    features: [
      [{ name: '2D', value: '2d' }, { name: '3D', value: '3d' }],
    ],
    price: 25,
    placeAnOrderButtonText: 'Choose Basic',

    whatYouWillGet: {
      titleTemplate: 'What do you get in the {0}?',
      blocks: [
        {
          image: 'whatYouWillGet/basic/2d.webp',
          title: 'Accurate 2D floor plan',
          description: 'This displays windows, doors, stove, and plumbing fixtures. You can also request room names and their areas in the order comments.',
          featureValues: ['2d'],
        },
        {
          image: 'whatYouWillGet/basic/dollhouse.webp',
          images: ['whatYouWillGet/basic/dollhouse.webp', ...Examples.floorPlans],
          title: 'All-angle 3D floor plan "dollhouse" with furniture',
          description: 'View it from multiple angles: top view image, images from various perspectives.',
          featureValues: ['3d'],
        },
      ],
      footerTitle: 'How do you receive the result?',
      footerBlocks: [
        { description: 'You can access all files, including the processed floor plan and virtual tour, through your email and personal account.' },
        { description: 'You can download an archive with interior renderings from your email.' },
      ],
    },
  },
  {
    tariff: TariffType.PlusSet,
    image: 'showcase/new/plus.webp',
    name: 'Plus set',
    summary: 'Basic set + 360° tour',
    previewFeature: '360',
    widgetType: 'iframe',
    hasEnvironmentType: true,
    features: [
      [{ name: '2D', value: '2d' }, { name: '3D', value: '3d' }, { name: '360°', modalName: '360° Tour', value: '360' }],
    ],
    price: 45,
    placeAnOrderButtonText: 'Choose Plus',

    whatYouWillGet: {
      titleTemplate: 'What do you get in the {0}?',
      blocks: [
        {
          image: 'whatYouWillGet/basic/2d.webp',
          title: 'Accurate 2D floor plan',
          description: 'A clear top-down view of rooms, dimensions and the overall layout.',
          featureValues: ['2d'],
        },
        {
          image: 'whatYouWillGet/plus/dollhouse.webp',
          images: ['whatYouWillGet/plus/dollhouse.webp', ...Examples.floorPlans],
          title: 'All-angle 3D floor plan',
          description: 'A furnished 3D dollhouse view of the uploaded floor plan.',
          featureValues: ['3d'],
        },
        {
          image: 'whatYouWillGet/plus/virtualTour.webp',
          title: 'Virtual Tour',
          description: 'Explore properties on any device, with immersive VR option available, featuring measurement tools for an engaging, informative experience.',
          featureValues: ['360'],
        },
      ],
      footerTitle: 'How do you receive the result?',
      footerBlocks: [
        { description: 'You can access all files, including the processed floor plan and virtual tour, through your email and personal account.' },
        { description: 'You can download an archive with interior renderings from your email.' },
      ],
    },
  },
  {
    tariff: TariffType.MaxRenderSet,
    type: ['bestValue'],
    image: 'showcase/new/render.webp',
    name: 'Pro set',
    summary: 'Plus set + renders & White Label',
    previewFeature: 'renderings',
    bestValueText: 'Best value',
    widgetType: 'iframe',
    hasEnvironmentType: true,
    hasBranding: true,
    features: [
      [{ name: '2D', value: '2d' }, { name: '3D', value: '3d' }, { name: '360°', modalName: '360° Tour', value: '360' }],
      [{ name: 'Renders', value: 'renderings' }, { name: 'White Label', value: 'whiteLabel' }],
    ],
    price: 69,
    placeAnOrderButtonText: 'Choose Pro',

    whatYouWillGet: {
      titleTemplate: 'What do you get in the {0}?',
      blocks: [
        {
          image: 'whatYouWillGet/basic/2d.webp',
          title: 'Accurate 2D floor plan',
          description: 'A clear top-down view of rooms, dimensions and the overall layout.',
          featureValues: ['2d'],
        },
        {
          image: 'whatYouWillGet/render/dollhouse.webp',
          images: ['whatYouWillGet/render/dollhouse.webp', ...Examples.floorPlans],
          title: 'All-angle 3D floor plan',
          description: 'A furnished 3D dollhouse view of the uploaded floor plan.',
          featureValues: ['3d'],
        },
        {
          image: 'whatYouWillGet/plus/virtualTour.webp',
          title: 'Virtual Tour',
          description: 'Explore properties on any device, with immersive VR option available, featuring measurement tools for an engaging, informative experience.',
          featureValues: ['360'],
        },
        {
          image: 'whatYouWillGet/render/renderings.webp',
          title: 'Interior Renderings',
          images: Examples.interiors,
          description: "High-quality, rectangular images showcase the property's interior features. These visuals enhance the property's presentation on websites.",
          featureValues: ['renderings'],
        },
        {
          image: 'whatYouWillGet/render/whiteLabel.webp',
          title: 'White Label',
          description: 'Present the virtual tour with your logo and website instead of Getfloorplan branding.',
          featureValues: ['whiteLabel'],
        },
      ],
      footerTitle: 'How do you receive the result?',
      footerBlocks: [
        { description: 'You can access all files, including the processed floor plan and virtual tour, through your email and personal account.' },
        { description: 'You can download an archive with interior renderings from your email.' },
      ],
    },
  },
  {
    tariff: TariffType.MultiSetPro,
    image: 'showcase/new/multi.webp',
    name: 'Multiset',
    summary: 'Pro set in 3 interior styles',
    previewFeature: 'multiStyle',
    widgetType: 'iframe',
    styleCount: 3,
    hasEnvironmentType: true,
    hasBranding: true,
    features: [
      [{ name: '2D', value: '2d' }, { name: '3D', value: '3d' }, { name: '360°', modalName: '360° Tour', value: '360' }],
      [{ name: 'Renders', value: 'renderings' }, { name: '3 Styles', value: 'multiStyle' }, { name: 'White Label', value: 'whiteLabel' }],
    ],
    price: 115,
    placeAnOrderButtonText: 'Choose Multiset',

    whatYouWillGet: {
      titleTemplate: 'What do you get in the {0}?',
      blocks: [
        {
          image: 'whatYouWillGet/basic/2d.webp',
          title: 'Accurate 2D floor plan',
          description: 'A clear top-down view of rooms, dimensions and the overall layout.',
          featureValues: ['2d'],
        },
        {
          image: 'whatYouWillGet/multi/dollhouse.webp',
          images: ['whatYouWillGet/multi/dollhouse.webp', ...Examples.floorPlans],
          title: 'All-angle 3D floor plan',
          description: 'A furnished 3D dollhouse view of the uploaded floor plan.',
          featureValues: ['3d'],
        },
        {
          image: 'whatYouWillGet/plus/virtualTour.webp',
          title: 'Virtual Tour',
          description: 'Explore properties on any device, with immersive VR option available, featuring measurement tools for an engaging, informative experience.',
          featureValues: ['360'],
        },
        {
          image: 'whatYouWillGet/render/renderings.webp',
          title: 'Interior Renderings',
          images: Examples.interiors,
          description: "High-quality rectangular images showcase the property's interior in every selected style.",
          featureValues: ['renderings'],
        },
        {
          image: 'whatYouWillGet/multi/multiStyle.webp',
          images: ['whatYouWillGet/multi/multiStyle.webp', ...Examples.interiors],
          title: 'Three interior styles',
          description: 'Receive the Pro package in three different interior styles for one uploaded plan.',
          featureValues: ['multiStyle'],
        },
        {
          image: 'whatYouWillGet/render/whiteLabel.webp',
          title: 'White Label',
          description: 'Present the virtual tour with your logo and website instead of Getfloorplan branding.',
          featureValues: ['whiteLabel'],
        },
      ],
      additionalInfo: {
        title: 'Revision is included',
        blocks: [
          { image: 'whatYouWillGet/multi/chair.svg', title: 'Changing several furniture units' },
          { image: 'whatYouWillGet/multi/door.svg', title: 'Replace doors/windows' },
          { image: 'whatYouWillGet/multi/brush.svg', title: 'Adding something regarding the style' },
        ],
      },
      footerTitle: 'How do you receive the result?',
      footerBlocks: [
        { description: 'You can access all files, including the processed floor plan and virtual tour, through your email and personal account.' },
        { description: 'You can download an archive with interior renderings from your email.' },
      ],
    },
  },
];

export const tariffs = { en, ru: en };
